import React, { useState } from "react";
import { toast } from "sonner";
import { ListPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { CONTROL_STYLES, scoreTone } from "@/lib/vendors";
import { Panel } from "@/components/rights/RightsBadges";
import { EvidenceUploadButton } from "@/components/rights/EvidenceUploadButton";
import { ContractBadge } from "@/components/vendors/VendorBadges";
import { Field, Pick } from "@/components/vendors/NewVendorDialog";

const ControlRow = ({ v, c, meta, canEdit, onChanged }) => {
  const [notes, setNotes] = useState(c.notes || "");
  const [clause, setClause] = useState(c.clause_reference || "");
  const patch = async (body, msg) => {
    try { await api.patch(`/vendors/${v.id}/contract-checks/${c.control_key}`, body); if (msg) toast.success(msg); onChanged(); }
    catch (e) { toast.error(apiError(e, "Update failed")); }
  };
  const raise = async () => {
    try { const { data } = await api.post(`/vendors/${v.id}/contract-checks/${c.control_key}/task`, {}); toast.success(`${data.task_ref} raised`); onChanged(); }
    catch (e) { toast.error(apiError(e, "Could not raise task")); }
  };
  const gap = ["NOT_MET", "PARTIAL"].includes(c.status);
  return (
    <div className="py-3 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_140px_130px_minmax(0,1fr)_auto] gap-2 items-center" data-testid={`contract-control-${c.control_key}`}>
      <div className="text-sm text-[#003366] font-medium">{c.label}
        <div className={`text-[11px] font-semibold ${CONTROL_STYLES[c.status]}`}>{meta.control_statuses[c.status]}{c.reviewed_by && <span className="font-normal text-slate-400"> · {c.reviewed_by}</span>}</div>
      </div>
      <Pick value={c.status} disabled={!canEdit} options={Object.entries(meta.control_statuses)} testId={`contract-status-${c.control_key}`}
            onChange={(s) => patch({ status: s, notes }, "Control updated")} />
      <Input value={clause} disabled={!canEdit} placeholder="Clause ref" className="h-9 text-xs" data-testid={`contract-clause-${c.control_key}`}
             onChange={(e) => setClause(e.target.value)} onBlur={() => clause !== (c.clause_reference || "") && patch({ clause_reference: clause })} />
      <Input value={notes} disabled={!canEdit} placeholder={gap ? "Gap note (required)" : "Notes"} className="h-9 text-xs" data-testid={`contract-notes-${c.control_key}`}
             onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (c.notes || "") && patch({ notes })} />
      <div className="flex items-center gap-1.5 justify-end">
        <span className="text-[10px] font-mono text-slate-400">{c.evidence_file_ids?.length || 0}</span>
        {canEdit && <EvidenceUploadButton endpoint={`/vendors/${v.id}/evidence`} category="CONTRACT_CONTROL" fields={{ control_key: c.control_key }}
                                          onUploaded={onChanged} label="Evidence" testId={`contract-attach-${c.control_key}`} />}
        {canEdit && gap && !c.task_id && (
          <Button size="sm" variant="outline" className="h-7 text-[11px] px-2" onClick={raise} data-testid={`contract-task-${c.control_key}`}>
            <ListPlus className="w-3 h-3 mr-1" /> Task</Button>
        )}
        {c.task_id && <span className="text-[10px] text-[#008080]" data-testid={`contract-task-linked-${c.control_key}`}>task raised</span>}
      </div>
    </div>
  );
};

export const ContractPanel = ({ v, meta, canEdit, onChanged }) => {
  const [dates, setDates] = useState({ contract_start: v.contract_start || "", contract_expiry: v.contract_expiry || "",
    breach_notice_hours: v.breach_notice_hours ?? "" });
  const patch = async (body, msg = "Contract updated") => {
    try { await api.patch(`/vendors/${v.id}`, body); toast.success(msg); onChanged(); } catch (e) { toast.error(apiError(e, "Update failed")); }
  };
  const blur = (k) => () => {
    const val = k === "breach_notice_hours" ? (dates[k] === "" ? null : Number(dates[k])) : dates[k] || null;
    if (val !== (v[k] ?? null)) patch({ [k]: val });
  };
  const s = v.contract_score;
  return (
    <Panel eyebrow="Contract controls" title="Contract" testId="contract-panel"
           actions={<><ContractBadge status={v.contract_status} label={v.contract_status_label} testId="contract-status-badge" />
             <span className={`text-sm font-mono font-bold ${scoreTone(s.score)}`} data-testid="contract-score">{s.score == null ? "—" : `${s.score}%`}</span></>}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pb-4 border-b border-slate-100">
        <Field label="Contract start"><Input type="date" value={dates.contract_start} disabled={!canEdit} className="h-9 text-xs" data-testid="contract-start-input"
               onChange={(e) => setDates((d) => ({ ...d, contract_start: e.target.value }))} onBlur={blur("contract_start")} /></Field>
        <Field label="Contract expiry"><Input type="date" value={dates.contract_expiry} disabled={!canEdit} className="h-9 text-xs" data-testid="contract-expiry-input"
               onChange={(e) => setDates((d) => ({ ...d, contract_expiry: e.target.value }))} onBlur={blur("contract_expiry")} /></Field>
        <Field label="Breach notice (hours)"><Input type="number" min={1} value={dates.breach_notice_hours} disabled={!canEdit} className="h-9 text-xs font-mono"
               data-testid="contract-breach-hours" onChange={(e) => setDates((d) => ({ ...d, breach_notice_hours: e.target.value }))} onBlur={blur("breach_notice_hours")} /></Field>
        <Field label="Terminated">
          <div className="h-9 flex items-center"><Switch checked={!!v.contract_terminated} disabled={!canEdit} data-testid="contract-terminated-switch"
                 onCheckedChange={(c) => patch({ contract_terminated: c }, c ? "Contract marked terminated" : "Contract reinstated")} /></div>
        </Field>
      </div>
      <div className="flex gap-4 text-[11px] text-slate-600 py-3" data-testid="contract-summary">
        <span><b className="font-mono text-emerald-700">{s.met}</b> met</span><span><b className="font-mono text-amber-700">{s.partial}</b> partial</span>
        <span><b className="font-mono text-red-600">{s.not_met}</b> not met</span><span><b className="font-mono text-slate-500">{s.pending}</b> not reviewed</span>
      </div>
      <div className="divide-y divide-slate-100">
        {v.contract_checks.map((c) => <ControlRow key={`${c.control_key}-${c.status}`} v={v} c={c} meta={meta} canEdit={canEdit} onChanged={onChanged} />)}
      </div>
    </Panel>
  );
};
